/* global angular */
(function (angular) {
    'use strict';

    /**
     * @ngdoc service
     * @name BaseFileBrowserController
     *
     * @description
     * BaseFileBrowserController is a base class for FileBrowserController and SearchController. It contains common resuable logic for both views
     */
    angular.module('GorillaBoy')
    .factory('BaseFileBrowserController', [
        '$rootScope',
        '$q',
        '$log',
        '$translate',
        '$filter',
        '$location',
        '$timeout',
        'AppConstants',
        'FavoritesService',
        'MessageManager',
        'DateTimeService',
        'UserCollectionService',
        'FeatureManagerService',
        'Features',
        'SignalProvider',
        'ContentTypeFormatter',
        'ResourceIconService',
        'DocumentSourceUrlController',
        'DocumentUtilities',
        BaseFileBrowserController
    ]);

    function BaseFileBrowserController($rootScope, $q, $log, $translate, $filter, $location, $timeout, AppConstants,
        FavoritesService, MessageManager, DateTimeService, UserCollectionService, FeatureManagerService, Features, SignalProvider,
        ContentTypeFormatter, ResourceIconService, DocumentSourceUrlController, DocumentUtilities) {
        var $scope = null,
            userInfo = null,
            viewTypes = AppConstants.ViewTypes,
            TYPE_FOLDER = 'FOLDER',
            TYPE_DOCUMENT = 'DOCUMENT';

        return {
            initScope : function(scopeVal, view, userInfo) {
                $scope = scopeVal;
                $scope.$on("$destroy", function() {
                    //Even though is a constant leak, we don't want to leak memory at all!
                    $scope = null;
                    userInfo = null;
                });
                // Keep track of selected items
                $scope.selectedFolders = {};
                $scope.selectedDocuments = {};

                DocumentSourceUrlController.initScope($scope);

                // A map keyed by the document/folder ID and will contain whether a dropdown for that document/folder is,
                // 1. open/collapsed
                // 2. Show more options are shown/hidden etc.,
                $scope.dropdownState = {};
                $scope.clickedOnResource  = function(resource) {
                    // No-op
                };
                $scope.toggleDropdownVisibility = function(resourceId) {
                    if (!$scope.dropdownState[resourceId]) {
                        return;
                    }
                    $scope.dropdownState[resourceId].isVisible = !$scope.dropdownState[resourceId].isVisible;
                };
                $scope.resetDropdownState = function(resourceId, resourceType) {
                    $scope.dropdownState[resourceId] = {
                        showMoreOptions : false,
                        isVisible : false,
                        resourceType: resourceType
                    };
                };

                // Register utility functions for getting icons
                $scope.getFolderIcon = ResourceIconService.getFolderIcon;
                $scope.getFileIcon = ResourceIconService.getFileIcon;

                /**
                 * This function will take resources array (folders/documents) and calculate the 'isFavorite'
                 * property of each resource
                 */
                function applyFavoritesToResourceList(resourceList, favoritesList) {
                    resourceList.forEach(function(resource) {
                        resource.isFavorite = (favoritesList.indexOf(resource.Id) !== -1);
                    });
                }

                /**
                 * Apply favorites information to the data that is passed in
                 */
                $scope.applyFavorites = function(documents, folders, Favorites, dontRefreshCache) {
                    if (!FeatureManagerService.isFeatureEnabled(Features.UI.FAVORITES)) {
                        return;
                    }
                    applyFavoritesToResourceList(folders, Favorites.Folders);
                    applyFavoritesToResourceList(documents, Favorites.Documents);
                    if (dontRefreshCache) {
                        return;
                    }
                    FavoritesService.getUserFavorites().then(function(data) {
                        applyFavoritesToResourceList(folders, data.Folders);
                        applyFavoritesToResourceList(documents, data.Documents);
                        //Update the local closure variable for further use. If we don't update then it'll cause a blinking effect
                        //where everytime you toggle thumbnail/list view you'll see blinking stars :)
                        Favorites = data;
                    });
                };

                $scope.getResourceRedirectUrl = function(document) {
                    var redirectUrl;
                    if (FeatureManagerService.isFeatureEnabled(Features.DocumentViewer.ONLINENOTES) && document.DocumentCategory === viewTypes.NOTE_TYPE) {
                        userInfo.NoteInfo.LastActiveNoteId = document.Id;
                        redirectUrl = '#/myNote';
                    } else if (DocumentUtilities.isQuartzDocument(document)) {
                      redirectUrl = "#/quartz/" + encodeURIComponent(document.Id);
                    } else {
                        redirectUrl = '#/document/' + encodeURIComponent(document.Id);
                    }
                    return redirectUrl;
                };

                var toggleFavoritePendingPromisePerResource = {};
                $scope.toggleFavorite = function(resource) {
                    // There should be at most one service call per resources in-flight. Otherwise the user can get into 409 situation (mainly because of throttles and jittered scheduling of retries)
                    if (toggleFavoritePendingPromisePerResource[resource.Id]) {
                        toggleFavoritePendingPromisePerResource[resource.Id].then(function() {
                            $scope.toggleFavorite(resource);
                        }); // If the promise rejects, it's assumed because of service conflict so don't retry other operations
                        return;
                    }

                    var View = $scope.layout.view;
                    var FolderContentTypes = AppConstants.FolderContentTypes;
                    var resourceType = resource.VersionMetadata ? FolderContentTypes.DOCUMENT : FolderContentTypes.FOLDER;
                    var currentValue = resource.isFavorite;
                    resource.isFavorite = !currentValue;

                    function favoriteApiCallSuccessHandler(data) {
                        toggleFavoritePendingPromisePerResource[resource.Id] = null;
                        return data;
                    }

                    function favoriteApiCallErrorHandler(error) {
                        toggleFavoritePendingPromisePerResource[resource.Id] = null;
                        return $q.reject(error);
                    }

                    var pendingPromise;
                    if (resource.isFavorite) {
                        pendingPromise = FavoritesService.createFav(resource.Id, resourceType)
                            .then(favoriteApiCallSuccessHandler, favoriteApiCallErrorHandler)
                            .then(null, createApiCallFailureHandler);
                    } else {
                        pendingPromise = FavoritesService.deleteFav(resource.Id, resourceType)
                            .then(favoriteApiCallSuccessHandler, favoriteApiCallErrorHandler)
                            .then(function(data) {
                                if (View === viewTypes.FAVORITES) {
                                    // Trying to derive $scope.documents or $scope.folders
                                    var propName = resourceType.toLowerCase() +'s';
                                    var removedItem;
                                    $scope[propName] = $scope[propName].filter(function(res) {
                                        var result = res.Id !== resource.Id;
                                        if (!result) {
                                            removedItem = res;
                                        }
                                        return result;
                                    });
                                    // deselect item if the deleted item is selected
                                    if (resourceType === FolderContentTypes.DOCUMENT) {
                                        $scope.fileManagerInstance.unselect('file', resource.Id);
                                    } else {
                                        $scope.fileManagerInstance.unselect('folder', resource.Id);
                                    }

                                    var createFav = function() {
                                        toggleFavoritePendingPromisePerResource[resource.Id] = FavoritesService.createFav(resource.Id, resourceType)
                                            .then(favoriteApiCallSuccessHandler, favoriteApiCallErrorHandler)
                                            .then(function(data) {
                                                removedItem.isFavorite = true;
                                                //add it back to the start of the list (if you add to the end user may not notice it if the list is long)
                                                $scope[propName] = [removedItem].concat($scope[propName]);
                                                return data;
                                            }, function(error) {
                                                if (error.status === 409 && _.startsWith(error.data.Message, 'EntityAlreadyExistsException')) {
                                                    removedItem.isFavorite = true;
                                                    //add it back to the start of the list (if you add to the end user may not notice it if the list is long)
                                                    $scope[propName] = [removedItem].concat($scope[propName]);
                                                } else {
                                                    MessageManager.addErrorMessage($translate.instant('GENERAL_MESSAGES.UNDO_FAILURE'));
                                                }
                                                return $q.reject(error);
                                            });
                                    };

                                    MessageManager.addSuccessMessage($translate.instant('FAVORITES.TOOLTIP_UNFAV_HTTP_SUCCESS', {
                                        ResourceName : resourceType === FolderContentTypes.DOCUMENT ? removedItem.VersionMetadata.Name : removedItem.Name
                                    }), true)
                                        .then(function() {
                                            // There should be at most one service call per resources in-flight. Otherwise the user can get into 409 situation (mainly because of throttles and jittered scheduling of retries)
                                            if (toggleFavoritePendingPromisePerResource[resource.Id]) {
                                                toggleFavoritePendingPromisePerResource[resource.Id].then(createFav, createFav);
                                            } else {
                                                createFav();
                                            }
                                        });
                                }
                                return data;
                            }, deleteApiCallFailureHandler);
                    }
                    toggleFavoritePendingPromisePerResource[resource.Id] = pendingPromise;

                    function deleteApiCallFailureHandler(error) {
                        if (error.status !== 404) {
                            // For 404 error, it suffices if we hide the error
                            MessageManager.addErrorMessage($translate.instant('FAVORITES.SAVE_FAILURE'));
                            // restore the value
                            resource.isFavorite = currentValue;
                        }
                        return $q.reject(error);
                    }

                    function createApiCallFailureHandler(error) {
                        if (error.status === 409 && _.startsWith(error.data.Message, 'EntityAlreadyExistsException')) {
                            // If it's 409 then favorite already exists, ignore the error (the resource is already marked as favorite, hence this call occurred, so nothing to do)
                        } else {
                            MessageManager.addErrorMessage($translate.instant('FAVORITES.SAVE_FAILURE'));
                            // otherwise restore the value
                            resource.isFavorite = currentValue;
                        }
                        return $q.reject(error);
                    }
                };

                $scope.openContainingFolderSupported = function() {
                    return $scope.fileManagerInstance.getTotalSelectedItemCount() === 1;
                };

                /*
                 * The below methods contain logic to determine Permissions for single file actions and the action bar.
                 */
                $scope.dropdownAvailable = function(docOrFolder) {
                    return !$scope.fileManagerInstance.isInTrash();
                };

                /**
                 * Occurs when the user clicks on the "Open Containing Folder" button in the top action bar.
                 */
                $scope.onActionBarOpenContainingFolder = function() {
                    var selectedItem;

                    if ($scope.selectedFileCount !== 1) {
                        // The button shouldn't be available if 0 or 2+ files are selected, but better safe than sorry.
                        return;
                    }

                    selectedItem = $scope.__getSingleSelectedItem();
                    $log.debug(selectedItem);
                    if (selectedItem) {
                        var resourceId = selectedItem.value.ParentFolderId;
                        $location.url('/folder/' + resourceId + '?fromSearch=true&highlightResourceId=' + selectedItem.value.Id);
                    }
                };

                $scope.getCheckoutUsersInfo = function(documents) {
                    var pendingUsers = [];
                    documents.forEach(function(doc) {
                        if (doc.CheckoutInfo && !doc.CheckoutInfo.CheckoutUserName) {
                            pendingUsers.push(doc.CheckoutInfo.CheckoutUserId);
                        }
                    });

                    pendingUsers = _.uniq(pendingUsers);
                    if (pendingUsers.length) {
                        UserCollectionService.getUserCollection(pendingUsers)
                        .then(function(Users) {
                            Users.forEach(function(User) {
                                var Id = User.Id;
                                documents.forEach(function(doc) {
                                    if (doc.CheckoutInfo && doc.CheckoutInfo.CheckoutUserId === Id) {
                                        //We need to translate the GivenName/Surname to Full Name conversion, because in different languages the Full Name conversion is done differently
                                        doc.CheckoutInfo.CheckoutUserName = $translate.instant('DOCUMENT_VIEW.VERSION.AUTHOR', {
                                            givenName : User.GivenName,
                                            surName : User.Surname
                                        });
                                    }
                                });
                            });
                        });
                    }
                };

                /* functions to get columns values (owner name, last modified, size)
                   for FileBrowser and Search controllers in list_view and thumbnail_view */
                $scope.getOwnerFullname = function(docOrFolder) {
                    var owner = docOrFolder.Owner;

                    if (!owner && $scope.folderState.inOwnFolder) {
                        owner = userInfo;
                    }

                    if (owner) {
                        var givenName = owner.GivenName;
                        if (givenName) {
                            return $scope.getFullName(owner);
                        }
                        return owner.Username;
                    } else {
                        // Permission V2 resource collection API does not provide an Owner info.
                        // Show an empty string until an Owner info download completes.
                        return '...';
                    }
                };

                $scope.getReadableDate = DateTimeService.getHumanReadableDate;

                $scope.getLastModified = function(resource) {
                    // VersionMetadata is more up to date for file because the newly uploaded version
                    // only update VersionMetadata. Folder does not have VersionMetadata.
                    if (resource.VersionMetadata) {
                        // for file
                        return resource.VersionMetadata.Modified;
                    } else {
                        // for folder
                        return resource.Modified;
                    }
                };

                $scope.getReadableSize = function(file) {
                     if (file && file.VersionMetadata) {
                        return file.VersionMetadata.Size;
                    } else {
                        return -1;
                    }
                };

                $scope.getReadableFolderSize = function(folder) {
                    if (folder) {
                       return folder.Size;
                   } else {
                       return -1;
                   }
                };

                $scope.getReadableType = function(file) {
                    var typeName;

                    if (!file) {
                        return '';
                    }

                    // Determine folder vs single file.
                    if (file.hasOwnProperty('VersionMetadata')) {
                        typeName = ContentTypeFormatter.getTypeCategory(file.VersionMetadata.ContentType);
                    } else {
                        typeName = $translate.instant('FILE_TYPES.FOLDER');
                    }

                    // TODO: fetch string from Localization Manager
                    return typeName;
                };

                /**
                *   Function to get URL of document
                */
                $scope.getFileRedirectUrl = function(document) {
                    var redirectUrl;
                    if (document.DocumentCategory === viewTypes.NOTE_TYPE) {
                        userInfo.NoteInfo.LastActiveNoteId = document.Id;
                        redirectUrl = '#/myNote';
                    } else if (DocumentUtilities.isQuartzDocument(document)) {
                      redirectUrl = "#/quartz/" + encodeURIComponent(document.Id);
                    } else {
                        redirectUrl = '#/document/' + encodeURIComponent(document.Id);
                    }

                    return redirectUrl;
                };

                /**
                 * Returns the index of the specified item with respect to its `$scope.{store}` array.
                 */
                $scope.__indexOfFileOrFolder = function(store, id) {
                    var i, arr = $scope[store];

                    if (arr === undefined) {
                        throw 'No such property on $scope: "' + store + '"';
                    }

                    if (arr.length === 0) {
                        return -1;
                    }

                    for (i = 0; i < arr.length; i += 1) {
                        if (arr[i].Id === id) {
                            return i;
                        }
                    }

                    return -1;
                };


                $scope.__getSingleSelectedItem = function() {
                    var selectedFiles, selectedFolders, type, itemId, item;

                    selectedFiles = $scope.fileManagerInstance.selectedFiles();
                    if (selectedFiles.length > 0) { // It's a file that's selected
                        type = TYPE_DOCUMENT;
                        itemId = selectedFiles[0];
                    } else {
                        selectedFolders = $scope.fileManagerInstance.selectedFolders();
                        if (selectedFolders.length > 0) {
                            type = TYPE_FOLDER;
                            itemId = selectedFolders[0];
                        }
                    }

                    if (itemId) {
                        item = $scope.__getFileById(type, itemId);
                        return {
                            type: type,
                            value: item
                        };
                    }

                    return null;
                };

                /**
                 * Returns the Document or Folder matching the specified identifier.
                 *   type: 'document' to indicate a document search or 'folder' for a folder search.
                 *   id: the identifier of the file or folder to retrieve.
                 */
                $scope.__getFileById = function(type, id) {
                    var store, index;

                    if (type !== TYPE_DOCUMENT && type !== TYPE_FOLDER) {
                        throw 'Unrecognized type. Expected "document" or "folder"';
                    }

                    store = (type === TYPE_DOCUMENT ? 'documents' : 'folders');
                    index = $scope.__indexOfFileOrFolder(store, id);

                    if (index < 0) {
                        return null;
                    }

                    return $scope[store][index];
                };

                $scope.openSupported = function() {
                    return $scope.fileManagerInstance.getTotalSelectedItemCount() === 1;
                };

                $scope.$on('fileSelect', function(event, eventArgs) {
                    $scope.selectedFileCount += 1;
                });

                $scope.$on('fileUnselect', function(event, eventArgs) {
                    // Ensure we never go negative
                    if ($scope.selectedFileCount > 0) {
                        $scope.selectedFileCount -= 1;
                    }
                });

                $scope.$on('fileUnselectAll', function(event) {
                    $timeout(function() {
                        $scope.selectedFileCount = 0;
                    });
                });

                //Single file actions in top action bar
                $scope.onActionBarShowActivity = function() {
                    var selectedItem;
                    if ($scope.selectedFileCount !== 1) {
                        //The button shouldn't be available if 0 or 2+ files are selected, but better safe than sorry.
                        return;
                    }
                    selectedItem = $scope.__getSingleSelectedItem();
                    if (selectedItem) {
                        var resourceId = selectedItem.value.Id;
                        if (selectedItem.type === TYPE_DOCUMENT) {
                            $location.url('/activities?documentId=' + resourceId + '&resourceName=' + selectedItem.value.VersionMetadata.Name);
                        } else {
                            $location.url('/activities?folderId=' + resourceId + '&resourceName=' + selectedItem.value.Name);
                        }
                    }
                };

                $scope.exclusivelySelect = function(itemType, item, $event) {
                    $scope.fileManagerInstance.clearSelected();
                    $scope.fileManagerInstance.markAsSelected(itemType, item.Id);
                    if ($event) {
                        $event.stopPropagation();
                    }
                };

                $scope.toggleItemSelection = function(itemType, item, $event) {
                    if ($scope.fileManagerInstance.isSelected(itemType, item.Id)) {
                        $scope.fileManagerInstance.unselect(itemType, item.Id);
                    } else {
                        $scope.fileManagerInstance.markAsSelected(itemType, item.Id);
                    }
                    if ($event) {
                        $event.stopPropagation();
                    }
                };

                $scope.onShowActivityClicked = function(selectedItem) {
                    if(selectedItem.type === TYPE_DOCUMENT) {
                        $location.url('/activities?documentId=' + selectedItem.item.Id + '&resourceName=' + selectedItem.item.VersionMetadata.Name);
                    } else {
                        $location.url('/activities?folderId=' + selectedItem.item.Id + '&resourceName=' + selectedItem.item.Name);
                    }
                };

                $scope.$on('fileSelect', syncSelectedDocumentsAndFolders);
                $scope.$on('fileUnselect', syncSelectedDocumentsAndFolders);
                $scope.$on('fileUnselectAll', syncSelectedDocumentsAndFolders);
                $scope.$on('fileSelectAll', syncSelectedDocumentsAndFolders);

                function syncSelectedDocumentsAndFolders() {
                    // Flush the local copy of selected items
                    $scope.selectedFolders = {};
                    $scope.selectedDocuments = {};
                    // Sync the local copy with file manager
                    angular.forEach($scope.fileManagerInstance.selectedFolders(), function(folderId) {
                        $scope.selectedFolders[folderId] = $scope.__getFileById(TYPE_FOLDER, folderId);
                    });

                    angular.forEach($scope.fileManagerInstance.selectedFiles(), function(documentId) {
                        $scope.selectedDocuments[documentId] = $scope.__getFileById(TYPE_DOCUMENT, documentId);
                    });

                    $rootScope.$broadcast(SignalProvider.Selection.SelectionUpdated);
                }
            }
        };
    }
}(angular));
